import { useEffect, useState, useMemo } from 'react';
import {
	addRouteListener,
	createRouterSelector,
	getRouterState,
} from '@atlassian/react-resource-router/src/controllers/router-store';
import { AsyncHorizontalOverviewNav } from '@atlassian/jira-business-horizontal-nav/src/async.tsx';
import { AsyncHorizontalNavJira } from '@atlassian/jira-horizontal-nav/src/async.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { AsyncHorizontalNavPlans } from '@atlassian/jira-horizontal-nav-plans/src/async.tsx';
import { useHorizontalNavComponent } from '@atlassian/jira-navigation-apps-horizontal-nav-selector/src/controllers/nav-component-controller/index.tsx';
import { getWillShowNav4 } from '@atlassian/jira-navigation-apps-sidebar-nav4-rollout-core/src/common/utils/get-will-show-nav4/index.tsx';
import { useSoftwareProjectTheming } from '@atlassian/jira-project-theme-providers/src/controllers/software-theme/index.tsx';
import {
	getIsSoftwareThemingEnabled,
	getIsSoftwareThemingM2Enabled,
} from '@atlassian/jira-project-theme-providers/src/utils/getIsSoftwareThemingEnabled.tsx';
import { functionWithCondition } from '@atlassian/jira-feature-flagging-utils';

export const useCurrentRouteName = createRouterSelector((state) => state.route.name);
export const useCurrentRouteGroup = createRouterSelector((state) => state.route.group);

export const getProjectTheme = ({
	route,
	HorizontalNavComponent,
	hasSoftwareTheming,
	isSoftwareFullTheming,
	isSoftwareProject,
}: {
	route: { name: string; group?: string };
	HorizontalNavComponent: ReturnType<typeof useHorizontalNavComponent>;
	hasSoftwareTheming: boolean;
	isSoftwareFullTheming: boolean;
	isSoftwareProject: boolean;
}) => {
	const routeName = route.name;
	const routeGroup = route.group;
	const isBusinessRouteGroup = routeGroup === 'classic-business';

	// eslint-disable-next-line no-nested-ternary
	const hasProjectViewNavigation = getWillShowNav4()
		? HorizontalNavComponent != null
		: fg('migrate_plans_navigation_to_relay')
			? HorizontalNavComponent === AsyncHorizontalNavJira ||
				HorizontalNavComponent === AsyncHorizontalOverviewNav ||
				HorizontalNavComponent === AsyncHorizontalNavPlans
			: HorizontalNavComponent === AsyncHorizontalNavJira ||
				HorizontalNavComponent === AsyncHorizontalOverviewNav;

	let hasTheming: boolean;
	if (getWillShowNav4()) {
		const isSoftwareCalendarRoute =
			routeName === 'software-calendar' || routeName === 'software-calendar-classic';

		if (isSoftwareCalendarRoute && !getIsSoftwareThemingM2Enabled()) {
			// remove this condition completely on m2 experiment cleanup
			hasTheming = false;
		} else if (isSoftwareProject) {
			hasTheming = hasSoftwareTheming;
		} else {
			hasTheming =
				HorizontalNavComponent === AsyncHorizontalNavJira ||
				HorizontalNavComponent === AsyncHorizontalOverviewNav ||
				routeName === 'issue';
		}
	} else {
		hasTheming = hasProjectViewNavigation;
	}

	// Routes showing a themed inset layout are:
	// - all routes with the business project view navigation
	// - select software routes with contoured theming
	// - except for the routes with business group (as they already cater for the theme)
	const isInset = hasTheming && !isBusinessRouteGroup && !isSoftwareFullTheming;

	return {
		isInset,
		hasTheming,
		hasProjectViewNavigation,
	};
};

const useProjectThemeNew = () => {
	const HorizontalNavComponent = useHorizontalNavComponent();
	const { hasSoftwareTheming, isSoftwareFullTheming, isSoftwareProject } =
		useSoftwareProjectTheming();
	const routerState = useMemo(() => getRouterState(), []);
	const [projectThemeState, setProjectThemeState] = useState(() =>
		getProjectTheme({
			route: {
				name: routerState.route.name,
				group: routerState.route.group,
			},
			HorizontalNavComponent,
			hasSoftwareTheming,
			isSoftwareFullTheming,
			isSoftwareProject,
		}),
	);

	useEffect(() => {
		const unsubscribe = addRouteListener(({ route }) => {
			setProjectThemeState((prevState) => {
				const { isInset, hasTheming, hasProjectViewNavigation } = getProjectTheme({
					route: {
						name: route.name,
						group: route.group,
					},
					HorizontalNavComponent,
					hasSoftwareTheming,
					isSoftwareFullTheming,
					isSoftwareProject,
				});
				if (
					prevState.isInset !== isInset ||
					prevState.hasTheming !== hasTheming ||
					prevState.hasProjectViewNavigation !== hasProjectViewNavigation
				) {
					return {
						isInset,
						hasTheming,
						hasProjectViewNavigation,
					};
				}
				return prevState;
			});
		});

		return () => unsubscribe();
	}, [
		setProjectThemeState,
		HorizontalNavComponent,
		isSoftwareFullTheming,
		isSoftwareProject,
		hasSoftwareTheming,
	]);

	return projectThemeState;
};

const useProjectThemeOld = () => {
	const routeName = useCurrentRouteName();
	const routeGroup = useCurrentRouteGroup();

	const HorizontalNavComponent = useHorizontalNavComponent();
	const { hasSoftwareTheming, isSoftwareFullTheming, isSoftwareProject } =
		getIsSoftwareThemingEnabled()
			? // eslint-disable-next-line react-hooks/rules-of-hooks
				useSoftwareProjectTheming()
			: { hasSoftwareTheming: false, isSoftwareFullTheming: false, isSoftwareProject: false };

	const isBusinessRouteGroup = routeGroup === 'classic-business';

	// eslint-disable-next-line no-nested-ternary
	const hasProjectViewNavigation = getWillShowNav4()
		? HorizontalNavComponent != null
		: fg('migrate_plans_navigation_to_relay')
			? HorizontalNavComponent === AsyncHorizontalNavJira ||
				HorizontalNavComponent === AsyncHorizontalOverviewNav ||
				HorizontalNavComponent === AsyncHorizontalNavPlans
			: HorizontalNavComponent === AsyncHorizontalNavJira ||
				HorizontalNavComponent === AsyncHorizontalOverviewNav;

	let hasTheming: boolean;
	if (getWillShowNav4()) {
		const isSoftwareCalendarRoute =
			routeName === 'software-calendar' || routeName === 'software-calendar-classic';

		if (isSoftwareCalendarRoute && !getIsSoftwareThemingM2Enabled()) {
			// remove this condition completely on m2 exp cleanup
			hasTheming = false;
		} else if (isSoftwareProject) {
			hasTheming = hasSoftwareTheming;
		} else {
			hasTheming =
				HorizontalNavComponent === AsyncHorizontalNavJira ||
				HorizontalNavComponent === AsyncHorizontalOverviewNav ||
				routeName === 'issue';
		}
	} else {
		hasTheming = hasProjectViewNavigation;
	}

	// Routes showing a themed inset layout are:
	// - all routes with the business project view navigation
	// - select software routes with contoured theming
	// - except for the routes with business group (as they already cater for the theme)
	const isInset = hasTheming && !isBusinessRouteGroup && !isSoftwareFullTheming;

	return {
		isInset,
		hasTheming,
		hasProjectViewNavigation,
	};
};

export const useProjectTheme = functionWithCondition(
	() => fg('reduce_navigation_rerenders_1'),
	useProjectThemeNew,
	useProjectThemeOld,
);
